import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Brain,
  MessageSquare,
  Handshake,
  Calendar,
  Download,
  Star,
  Zap,
  Shield,
  FileText,
  Settings,
  LucideIcon
} from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Day 8: Final Platform Polish & Launch Preparation
 * Standardized CTA System with Progressive Conversion Funnels
 */

export interface CTAProps {
  variant?: "hero-primary" | "hero-secondary" | "primary" | "secondary" | "tertiary" | "urgent" | "social-proof";
  size?: "sm" | "md" | "lg";
  icon?: LucideIcon;
  children: React.ReactNode;
  to?: string;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  external?: boolean;
  showArrow?: boolean;
  badge?: string;
  urgency?: string;
}

// Standardized CTA component with consistent styling
export function CTA({
  variant = "primary",
  size = "md",
  icon: Icon,
  children,
  to,
  href,
  onClick,
  disabled = false,
  className,
  external = false,
  showArrow = true,
  badge,
  urgency,
  ...props
}: CTAProps) {
  const baseClasses = "inline-flex items-center justify-center gap-2 font-heading font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed relative";

  const variants = {
    "hero-primary": "rounded-lg bg-accent px-8 py-4 text-base text-accent-foreground hover:bg-gold-dark hover:shadow-xl hover:scale-105 transform",
    "hero-secondary": "rounded-lg border-2 border-primary-foreground/30 bg-transparent px-8 py-4 text-base text-primary-foreground hover:bg-primary-foreground/10 hover:border-primary-foreground/50",
    "primary": "rounded-lg bg-accent px-6 py-3 text-sm text-accent-foreground hover:bg-gold-dark hover:shadow-lg",
    "secondary": "rounded-lg border border-accent/30 bg-transparent px-6 py-3 text-sm text-accent hover:bg-accent/5 hover:border-accent/50",
    "tertiary": "text-sm text-accent hover:text-gold-dark hover:underline underline-offset-4",
    "urgent": "rounded-lg bg-red-600 px-6 py-3 text-sm text-white hover:bg-red-700 hover:shadow-lg animate-pulse",
    "social-proof": "rounded-lg bg-green-600 px-6 py-3 text-sm text-white hover:bg-green-700 hover:shadow-lg"
  };

  const sizes = {
    sm: "px-4 py-2 text-xs",
    md: "px-6 py-3 text-sm",
    lg: "px-8 py-4 text-base"
  };

  const classes = cn(
    baseClasses,
    variants[variant],
    !variants[variant].includes("px-") && sizes[size],
    className
  );

  const content = (
    <>
      {badge && (
        <span className="absolute -top-2 -right-2 rounded-full bg-red-500 px-2 py-1 text-xs font-bold text-white">
          {badge}
        </span>
      )}
      {Icon && <Icon className="h-4 w-4" />}
      {children}
      {urgency && <span className="ml-2 text-xs opacity-90">({urgency})</span>}
      {showArrow && variant === "tertiary" && <ArrowRight className="h-3 w-3" />}
    </>
  );

  if (href || external) {
    return (
      <a
        href={href}
        className={classes}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        onClick={onClick}
        {...props}
      >
        {content}
      </a>
    );
  }

  if (to) {
    return (
      <Link to={to} className={classes} onClick={onClick} {...props}>
        {content}
      </Link>
    );
  }

  return (
    <button className={classes} onClick={onClick} disabled={disabled} {...props}>
      {content}
    </button>
  );
}

// Progressive Conversion Funnel CTAs

// Awareness Stage - Hero CTAs
export function HeroPrimaryCTA({
  children = "Get Free AI Assessment",
  ...props
}: Omit<CTAProps, "variant" | "children"> & { children?: React.ReactNode }) {
  return (
    <CTA variant="hero-primary" icon={Brain} {...props}>
      {children}
    </CTA>
  );
}

export function HeroSecondaryCTA({
  children = "Explore Our Services",
  ...props
}: Omit<CTAProps, "variant" | "children"> & { children?: React.ReactNode }) {
  return (
    <CTA variant="hero-secondary" {...props}>
      {children}
    </CTA>
  );
}

// Interest Stage - Service Exploration
export function ServiceDiscoveryCTA({
  children = "Discover Solutions",
  ...props
}: Omit<CTAProps, "variant" | "children"> & { children?: React.ReactNode }) {
  return (
    <CTA variant="primary" icon={Shield} {...props}>
      {children}
    </CTA>
  );
}

export function ToolTrialCTA({
  children = "Try AI Tools Free",
  ...props
}: Omit<CTAProps, "variant" | "children"> & { children?: React.ReactNode }) {
  return (
    <CTA variant="primary" icon={Zap} badge="FREE" {...props}>
      {children}
    </CTA>
  );
}

// Consideration Stage - Consultation & Evaluation
export function ConsultationCTA({
  children = "Book Free Consultation",
  urgency = "Limited slots",
  ...props
}: Omit<CTAProps, "variant" | "children" | "urgency"> & { children?: React.ReactNode; urgency?: string }) {
  return (
    <CTA variant="primary" icon={MessageSquare} urgency={urgency} {...props}>
      {children}
    </CTA>
  );
}

export function AssessmentCTA({
  children = "Get Custom Assessment",
  ...props
}: Omit<CTAProps, "variant" | "children"> & { children?: React.ReactNode }) {
  return (
    <CTA variant="secondary" icon={Download} {...props}>
      {children}
    </CTA>
  );
}

// Decision Stage - Partnership & Commitment
export function PartnershipCTA({
  children = "Start Partnership",
  ...props
}: Omit<CTAProps, "variant" | "children"> & { children?: React.ReactNode }) {
  return (
    <CTA variant="primary" icon={Handshake} {...props}>
      {children}
    </CTA>
  );
}

export function ProposalCTA({
  children = "Request Proposal",
  urgency = "Custom quote",
  ...props
}: Omit<CTAProps, "variant" | "children" | "urgency"> & { children?: React.ReactNode; urgency?: string }) {
  return (
    <CTA variant="urgent" icon={FileText} urgency={urgency} {...props}>
      {children}
    </CTA>
  );
}

// Social Proof & Urgency CTAs
export function SocialProofCTA({
  children = "Join 150+ Organizations",
  ...props
}: Omit<CTAProps, "variant" | "children"> & { children?: React.ReactNode }) {
  return (
    <CTA variant="social-proof" icon={Star} {...props}>
      {children}
    </CTA>
  );
}

export function UrgentCTA({
  children = "Limited Time Offer",
  urgency = "Ends soon",
  ...props
}: Omit<CTAProps, "variant" | "children" | "urgency"> & { children?: React.ReactNode; urgency?: string }) {
  return (
    <CTA variant="urgent" icon={Calendar} urgency={urgency} {...props}>
      {children}
    </CTA>
  );
}

// CTA Groups for Different Pages/Contexts

export function HeroCTAGroup({ className }: { className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className={cn("flex flex-col gap-4 sm:flex-row", className)}
    >
      <HeroPrimaryCTA to="/tools/compliance-advisor" />
      <HeroSecondaryCTA to="/services" />
      <SocialProofCTA to="/clients" className="sm:hidden lg:inline-flex" />
    </motion.div>
  );
}

export function ServiceCTAGroup({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-col gap-3 sm:flex-row sm:justify-center", className)}>
      <ConsultationCTA to="/schedule" />
      <PartnershipCTA to="/partnerships" />
    </div>
  );
}

export function ToolResultCTAGroup({
  onDownload,
  onRetake,
  className
}: {
  onDownload?: () => void;
  onRetake?: () => void;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-3 sm:flex-row sm:justify-center", className)}>
      {onDownload && (
        <CTA variant="primary" icon={Download} onClick={onDownload}>
          Download Full Report
        </CTA>
      )}
      <ProposalCTA to="/contact" />
      {onRetake && (
        <CTA variant="tertiary" onClick={onRetake}>
          Retake Assessment
        </CTA>
      )}
    </div>
  );
}

export function ConversionCTAGroup({ className }: { className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn("flex flex-col gap-3 sm:flex-row", className)}
    >
      <UrgentCTA to="/schedule" urgency="24h response" />
      <AssessmentCTA to="/contact" />
    </motion.div>
  );
}

export function WorkspaceCTAGroup({ className }: { className?: string }) {
  return (
    <div className={cn("grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4", className)}>
      <CTA variant="primary" icon={Brain} to="/workspace/tools" size="sm">
        AI Tools
      </CTA>
      <CTA variant="primary" icon={FileText} to="/workspace/projects" size="sm">
        Projects
      </CTA>
      <CTA variant="primary" icon={MessageSquare} to="/workspace/requests" size="sm">
        Requests
      </CTA>
      <CTA variant="secondary" icon={Settings} to="/workspace/settings" size="sm">
        Settings
      </CTA>
    </div>
  );
}
